import {
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faEye,
  faFilePdf,
  faLink,
  faPrint,
  faRobot,
  faShareNodes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { Link, useParams } from "react-router-dom";

import { api, ApiClientError, isAbortError } from "../api";
import { useDocumentTitle } from "../document-title";
import type { GuideDetail } from "./GuideDetailPage";

const positionLabels = {
  approve: "Support",
  disapprove: "Oppose",
  no_position: "No Position",
} as const;

export function GuideItemPage() {
  const { accountSlug = "", code = "", itemCode = "" } = useParams();
  const [guide, setGuide] = useState<GuideDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const loadedGuideKey = useRef("");
  const [shareState, setShareState] = useState<
    "idle" | "sharing" | "shared" | "copied"
  >("idle");

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setError("");
      const value = await api<GuideDetail>(
        `/api/guides/${encodeURIComponent(accountSlug)}/${encodeURIComponent(code)}?itemCode=${encodeURIComponent(itemCode)}`,
        { signal: signal ?? null },
      );
      setGuide(value);
    },
    [accountSlug, code, itemCode],
  );

  useEffect(() => {
    const controller = new AbortController();
    const guideKey = `${accountSlug}/${code}`;
    if (loadedGuideKey.current !== guideKey) {
      loadedGuideKey.current = guideKey;
      setGuide(null);
    }
    setLoading(true);
    void load(controller.signal)
      .catch((caught: unknown) => {
        if (isAbortError(caught)) return;
        setError(
          caught instanceof ApiClientError
            ? caught.message
            : "Unable to load agenda item.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [accountSlug, code, load]);

  const retryLoad = () => {
    void load().catch((caught: unknown) =>
      setError(
        caught instanceof ApiClientError
          ? caught.message
          : "Unable to load agenda item.",
      ),
    );
  };

  useEffect(() => {
    if (shareState !== "shared" && shareState !== "copied") return;
    const timer = window.setTimeout(() => setShareState("idle"), 2_000);
    return () => window.clearTimeout(timer);
  }, [shareState]);

  const item = useMemo(() => guide?.items[0] ?? null, [guide]);
  const showingRequestedItem =
    item?.code.toLocaleUpperCase() === itemCode.toLocaleUpperCase();

  useDocumentTitle(
    guide && item && showingRequestedItem
      ? `${item.agendaNumber || `Item ${item.code}`} | ${guide.ownerName}`
      : "Agenda item",
  );

  if (!guide || !item)
    return (
      <div className="page-section">
        <div className="container">
          {error ? (
            <div className="alert alert-error" role="alert">
              <p>{error}</p>
              <button
                className="button button-small button-secondary"
                type="button"
                onClick={retryLoad}
              >
                Try again
              </button>
            </div>
          ) : (
            <div className="loading-block" role="status">
              Loading agenda item…
            </div>
          )}
        </div>
      </div>
    );

  const rationaleRounds = item.rounds.filter((round) =>
    round.adoptionRationale.trim(),
  );

  const share = async () => {
    setError("");
    setShareState("sharing");
    try {
      if (navigator.share) {
        await navigator.share({
          title: item.agendaNumber || `Item ${item.code}`,
          url: window.location.href,
        });
        setShareState("shared");
      } else {
        if (!navigator.clipboard)
          throw new Error("Clipboard access is unavailable");
        await navigator.clipboard.writeText(window.location.href);
        setShareState("copied");
      }
    } catch (caught) {
      setShareState("idle");
      if (caught instanceof DOMException && caught.name === "AbortError")
        return;
      setError("Unable to share this agenda item.");
    }
  };

  const shareLabel =
    shareState === "sharing"
      ? "Sharing…"
      : shareState === "shared"
        ? "Shared"
        : shareState === "copied"
          ? "Copied"
          : "Share";

  const itemTarget = (targetCode: string) =>
    `/guides/${guide.accountSlug}/${guide.code}/items/${targetCode}`;

  return (
    <div
      className={`page-section item-page dense-record-page${loading || !showingRequestedItem ? " is-refreshing" : ""}`}
      aria-busy={loading}
    >
      <div className="container">
        <div className="record-command-bar">
          <Link
            className="back-link"
            to={`/guides/${guide.accountSlug}/${guide.code}`}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to guide
          </Link>
          <div
            className="record-command-actions item-toolbar"
            aria-label="Agenda item actions"
          >
            <button className="icon-action" onClick={() => window.print()}>
              <FontAwesomeIcon icon={faPrint} /> <span>Print</span>
            </button>
            <button
              className="icon-action"
              onClick={() => void share()}
              disabled={
                loading || !showingRequestedItem || shareState === "sharing"
              }
            >
              <FontAwesomeIcon icon={faShareNodes} /> <span>{shareLabel}</span>
            </button>
            <Link
              className="icon-action"
              to={`/guides/${guide.accountSlug}/${guide.code}#other-positions`}
            >
              <FontAwesomeIcon icon={faEye} /> <span>Other positions</span>
            </Link>
          </div>
        </div>

        <header className="item-header compact-record-header">
          <p className="kicker">{guide.ownerName} STANCEKIT</p>
          <h1>{item.agendaNumber || `Item #${item.code}`}</h1>
          <p className="item-guide-name">
            <Link to={`/guides/${guide.accountSlug}/${guide.code}`}>
              {guide.title}
            </Link>
          </p>
          <p>{item.description}</p>
          {rationaleRounds.length ? (
            <section
              className="item-rationale"
              aria-labelledby="item-rationale-title"
            >
              <h2 id="item-rationale-title">Rationale</h2>
              {rationaleRounds.map((round) => (
                <div key={round.id}>
                  {item.rounds.length > 1 ? <h3>{round.label}</h3> : null}
                  <p>{round.adoptionRationale}</p>
                </div>
              ))}
            </section>
          ) : null}
          <div className="item-provenance">
            {item.sourceUrl ? (
              <a href={item.sourceUrl} target="_blank" rel="noreferrer">
                <FontAwesomeIcon icon={faLink} />{" "}
                {item.sourceLabel || "Source agenda item"}
              </a>
            ) : null}
            {item.sourceAttachmentUrl ? (
              <a
                href={item.sourceAttachmentUrl}
                target="_blank"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={faFilePdf} /> Source pages
              </a>
            ) : null}
            {item.descriptionAiGenerated ? (
              <span className="ai-marker">
                <FontAwesomeIcon icon={faRobot} /> AI-assisted description
              </span>
            ) : null}
            <span>
              {new Date(`${guide.guideDate}T12:00:00`).toLocaleDateString()}
            </span>
            <span>Guide #{guide.code}</span>
            <span className="status-badge">{guide.visibility}</span>
          </div>
        </header>

        <nav className="item-sequence" aria-label="Agenda item navigation">
          {guide.previousItemCode ? (
            <Link to={itemTarget(guide.previousItemCode)}>
              <FontAwesomeIcon icon={faChevronLeft} /> Previous
            </Link>
          ) : (
            <span />
          )}
          <strong>
            {loading
              ? "Loading agenda item…"
              : `Position ${guide.currentItemIndex?.toLocaleString()} of ${guide.guideItemCount.toLocaleString()}`}
          </strong>
          {guide.nextItemCode ? (
            <Link to={itemTarget(guide.nextItemCode)}>
              Next <FontAwesomeIcon icon={faChevronRight} />
            </Link>
          ) : (
            <span />
          )}
        </nav>

        {error ? (
          <div className="alert alert-error compact-alert" role="alert">
            {error}
          </div>
        ) : null}

        <section
          className="round-list dense-round-list shared-position-list"
          aria-label="Hearing positions"
        >
          {item.rounds.map((round) => {
            const adoptedLabel =
              (round.adoptedChoice
                ? positionLabels[round.adoptedChoice]
                : null) ?? "Pending";
            return (
              <article className="position-work-row" key={round.id}>
                <header className="round-summary">
                  <span className="round-number">
                    {String(round.order).padStart(2, "0")}
                  </span>
                  <div>
                    <h2>{round.label}</h2>
                    {round.description ? <p>{round.description}</p> : null}
                  </div>
                  <span
                    className={`position-chip large ${round.adoptedChoice ?? "pending"}`}
                  >
                    {adoptedLabel}
                  </span>
                </header>
              </article>
            );
          })}
        </section>

        <nav
          className="item-sequence item-sequence-bottom"
          aria-label="Continue through agenda items"
        >
          {guide.previousItemCode ? (
            <Link to={itemTarget(guide.previousItemCode)}>
              <FontAwesomeIcon icon={faChevronLeft} /> Previous position
            </Link>
          ) : (
            <span />
          )}
          <Link to={`/guides/${guide.accountSlug}/${guide.code}`}>
            Return to guide
          </Link>
          {guide.nextItemCode ? (
            <Link to={itemTarget(guide.nextItemCode)}>
              Next position <FontAwesomeIcon icon={faChevronRight} />
            </Link>
          ) : (
            <span />
          )}
        </nav>
      </div>
    </div>
  );
}
